import { css } from '@emotion/react';
import {
  forwardRef, useCallback, useEffect, useState,
} from 'react';
import type { Theme } from '~/_shared/themes/theme.model';
import {
  createColorWithOpacity, guaranteeHash, isValidHex,
} from '../colorPicker.helpers';
import { type ColorResult } from '../colorPicker.types';

const DEFAULT_DIMENSION = 20;

const inputStyle = (theme: Theme) => css({
  height: DEFAULT_DIMENSION,
  textTransform: 'uppercase',
  width: '80%',
  '&[disabled]': {
    opacity: theme.opacity.disabled,
  },
});

type ColorCodeHexFieldProps = Readonly<{
  className?: string;
  disabled?: boolean;
  onChange?: (value: ColorResult) => void;
  value: ColorResult;
}>;

export const ColorCodeHexFieldComponent = forwardRef<HTMLInputElement, ColorCodeHexFieldProps>((props, inputRef) => {
  const [hexInput, setHexInput] = useState<string>(props.value.hex);

  useEffect(() => {
    setHexInput(props.value.hex);
  }, [props.value.hex]);

  const onHexInputChange = useCallback((value: string) => {
    const hex = guaranteeHash(value);

    // Only full length hex is valid to keep input consistent with ColorResult format
    if (hex.length === 7 && isValidHex(hex) && props.value.hex.toUpperCase() !== hex.toUpperCase()) {
      const onChange = props.onChange;
      onChange?.(createColorWithOpacity(hex, props.value.hsla.a));
    }
    else {
      setHexInput(value);
    }
  }, [props.onChange, props.value.hex, props.value.hsla.a]);

  return (
    <input
      className={props.className}
      css={inputStyle}
      disabled={props.disabled}
      maxLength={7}
      onChange={e => onHexInputChange(e.target.value)}
      ref={inputRef}
      value={guaranteeHash(hexInput)}
    />
  );
});
